import {
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import BrandMark from './BrandMark';
import { Icon } from './icons';
import { documentTitle, initials, NAV_GROUPS, SCREENS, type NavGroup, type ViewKind } from './nav';
import { applyTheme, nextTheme, readTheme, THEME_LABELS, type ThemePreference } from './theme';
import Tour from './Tour';
import {
  autoTour,
  markSeen,
  readSeen,
  resetSeen,
  safeStorage,
  TOURS,
  WELCOME,
  type TourId,
  type TourStep,
} from './tours';

const THEME_ICON = { auto: 'auto', light: 'sun', dark: 'moon' } as const;

export interface ShellSection {
  id: NavGroup;
  title: string | null;
  items: ViewKind[];
}

interface Props {
  sections: ShellSection[];
  current: ViewKind;
  /** الجولة الخاصة بما يظهر الآن — تبويب الإعدادات له جولته. بلا قيمة: لا جولات (معرض التصميم). */
  tourId?: TourId;
  onSelect: (kind: ViewKind) => void;
  companyName: string | null;
  userName: string;
  roleLabel: string | null;
  onLogout: () => void;
  /** شريط فوق المحتوى (الشركة للقراءة فقط مثلاً). */
  banner?: ReactNode;
  /** البحث الموحّد فى الشريط العلوى — بلا شركة لا يظهر. */
  search?: ReactNode;
  /** رابط كل شاشة — Ctrl+ضغطة أو الزرار الأوسط يفتحها فى تبويب جديد. */
  hrefOf?: (kind: ViewKind) => string;
  /** مسار الشاشة الفرعية تحت عنوانها: «العملاء ‹ كشف حساب العميل»، والأب يُضغط. */
  trail?: { label: string; onSelect?: () => void }[];
  /** رجوع خطوة — يظهر فى الشاشات الفرعية فقط. */
  onBack?: () => void;
  children: ReactNode;
}

const FOCUSABLE = 'button:not([disabled]), [href], input, select, [tabindex]:not([tabindex="-1"])';

/**
 * هيكل التطبيق: القائمة بمجموعاتها، والشريط العلوى، وعنوان الصفحة، وحاوية الشاشة التى
 * تتحرك عند الدخول. لا يعرف شيئًا عن الـ API — `App` يقرّر ما يظهر ويمرّره.
 */
export default function Shell({
  sections,
  current,
  tourId,
  onSelect,
  companyName,
  userName,
  roleLabel,
  onLogout,
  banner,
  search,
  hrefOf,
  trail,
  onBack,
  children,
}: Props) {
  const [navOpen, setNavOpen] = useState(false);
  const [theme, setTheme] = useState<ThemePreference>(readTheme);
  const asideRef = useRef<HTMLElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const [tour, setTour] = useState<TourStep[] | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const helpRef = useRef<HTMLDivElement>(null);

  // أول زيارة لكل شاشة تبدأ جولتها وحدها، بعد مهلة قصيرة حتى تُرسم الشاشة وبياناتها
  useEffect(() => {
    if (!tourId) return;
    const storage = safeStorage();
    const pending = autoTour(tourId, readSeen(storage));
    if (!pending) return;
    const timer = window.setTimeout(() => {
      markSeen(storage, pending.ids);
      setTour(pending.steps);
    }, 700);
    return () => window.clearTimeout(timer);
  }, [tourId]);

  useEffect(() => {
    if (!helpOpen) return;
    const onDown = (event: MouseEvent) => {
      if (!helpRef.current?.contains(event.target as Node)) setHelpOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setHelpOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [helpOpen]);

  function startTour(steps: readonly TourStep[]): void {
    setHelpOpen(false);
    setNavOpen(false);
    setTour([...steps]);
  }

  useEffect(() => {
    document.title = documentTitle(current, companyName);
  }, [current, companyName]);

  useEffect(() => {
    if (!navOpen) return;
    const aside = asideRef.current;
    // التركيز ينتقل للدرج ويُحبس فيه حتى يُغلق — Tab لا يهرب للصفحة المغطاة خلفه
    aside?.querySelector<HTMLButtonElement>('.nav-close')?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setNavOpen(false);
        toggleRef.current?.focus();
        return;
      }
      if (event.key !== 'Tab' || !aside) return;
      const focusable = Array.from(aside.querySelectorAll<HTMLElement>(FOCUSABLE));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen]);

  function select(kind: ViewKind, event?: ReactMouseEvent): void {
    // Ctrl/⌘ أو الزرار الأوسط: تبويب جديد كأى رابط، والشاشة الحالية تفضل كما هى
    // نسخة سطح المكتب نافذة واحدة بلا تبويبات: الفتح يُرفض فتُفتح الشاشة هنا كالعادة
    if (hrefOf && event && (event.ctrlKey || event.metaKey || event.button === 1)) {
      event.preventDefault();
      const opened = window.open(hrefOf(kind), '_blank');
      if (opened) {
        opened.opener = null;
        return;
      }
    } else if (event?.button === 1) return;
    setNavOpen(false);
    onSelect(kind);
  }

  function cycleTheme(): void {
    const next = nextTheme(theme);
    applyTheme(next);
    setTheme(next);
  }

  const meta = SCREENS[current];
  const groupTitle = NAV_GROUPS.find((group) => group.id === meta.group)?.title ?? null;

  return (
    <div className={'app' + (navOpen ? ' nav-open' : '')}>
      <div className="nav-backdrop" aria-hidden="true" onClick={() => setNavOpen(false)} />
      <aside id="app-nav" ref={asideRef} aria-label="القائمة الرئيسية">
        <div className="logo">
          <BrandMark />
          <span className="logo-text">
            حسابات<span className="logo-dot"> • </span>نواة
          </span>
          <button
            type="button"
            className="nav-close"
            aria-label="إغلاق القائمة"
            onClick={() => {
              setNavOpen(false);
              toggleRef.current?.focus();
            }}
          >
            <Icon name="close" />
          </button>
        </div>
        <nav className="nav-sections">
          {sections.map((section) => (
            <div key={section.id} className={'nav-section nav-' + section.id}>
              {section.title && <p className="nav-heading">{section.title}</p>}
              {section.items.map((kind) => (
                <button
                  key={kind}
                  type="button"
                  className="nav-item"
                  aria-current={current === kind ? 'page' : undefined}
                  onClick={(event) => select(kind, event)}
                  onAuxClick={(event) => select(kind, event)}
                >
                  <Icon name={SCREENS[kind].icon} />
                  <span>{SCREENS[kind].title}</span>
                </button>
              ))}
            </div>
          ))}
        </nav>
      </aside>

      <main>
        <div className="topbar">
          <button
            ref={toggleRef}
            type="button"
            className="nav-toggle"
            aria-label="فتح القائمة"
            aria-controls="app-nav"
            aria-expanded={navOpen}
            onClick={() => setNavOpen(true)}
          >
            <Icon name="menu" />
          </button>
          <p className="company-name">{companyName ?? 'لا توجد شركة بعد'}</p>
          {search}
          <div className="who">
            {tourId && (
              <div className="tour-help-wrap" ref={helpRef}>
                <button
                  type="button"
                  className="icon-button tour-help"
                  onClick={() => setHelpOpen((open) => !open)}
                  title="مساعدة وجولات تعريفية"
                  aria-label="مساعدة وجولات تعريفية"
                  aria-haspopup="menu"
                  aria-expanded={helpOpen}
                >
                  <Icon name="help" />
                </button>
                {helpOpen && (
                  <div className="tour-menu" role="menu">
                    <button type="button" role="menuitem" onClick={() => startTour(TOURS[tourId])}>
                      جولة هذه الشاشة
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => startTour([...WELCOME, ...TOURS[tourId]])}
                    >
                      الجولة التعريفية الكاملة
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        resetSeen(safeStorage());
                        setHelpOpen(false);
                      }}
                    >
                      أعد تشغيل الجولات فى كل الشاشات
                    </button>
                  </div>
                )}
              </div>
            )}
            <button
              type="button"
              className="icon-button"
              onClick={cycleTheme}
              title={'الوضع: ' + THEME_LABELS[theme]}
              aria-label={'الوضع: ' + THEME_LABELS[theme] + ' — اضغط للتبديل'}
            >
              <Icon name={THEME_ICON[theme]} />
            </button>
            <span className="user-chip">
              <span className="avatar" aria-hidden="true">
                {initials(userName)}
              </span>
              <span className="user-text">
                <span className="user-name">{userName}</span>
                {roleLabel && <span className="user-role">{roleLabel}</span>}
              </span>
            </span>
            <button
              type="button"
              className="icon-button"
              onClick={onLogout}
              title="تسجيل الخروج"
              aria-label="تسجيل الخروج"
            >
              <Icon name="logout" />
            </button>
          </div>
        </div>
        {banner}
        {/* المفتاح بنوع الشاشة: الانتقال يعمل عند تغيير الشاشة لا عند تغيير حسابٍ داخلها */}
        <div className="screen" key={current}>
          {current !== 'dashboard' && (
            <header className="page-header">
              {onBack ? (
                <button
                  type="button"
                  className="page-back"
                  onClick={onBack}
                  title="رجوع (Alt+←)"
                  aria-label="رجوع"
                >
                  <Icon name="chevron" size={22} />
                </button>
              ) : (
                <span className="page-icon" aria-hidden="true">
                  <Icon name={meta.icon} size={22} />
                </span>
              )}
              <div>
                {trail && trail.length > 0 ? (
                  <nav className="page-trail" aria-label="مكانك فى البرنامج">
                    {groupTitle && <span>{groupTitle}</span>}
                    {trail.map((step, index) => (
                      <span
                        key={index}
                        aria-current={index === trail.length - 1 ? 'page' : undefined}
                      >
                        {step.onSelect ? (
                          <button type="button" className="link" onClick={step.onSelect}>
                            {step.label}
                          </button>
                        ) : (
                          step.label
                        )}
                      </span>
                    ))}
                  </nav>
                ) : (
                  groupTitle && <p className="page-eyebrow">{groupTitle}</p>
                )}
                <h1>{meta.title}</h1>
                <p className="page-hint">{meta.hint}</p>
              </div>
            </header>
          )}
          {children}
        </div>
      </main>
      {tour && <Tour steps={tour} onClose={() => setTour(null)} />}
    </div>
  );
}
